
import Button from '@/components/atoms/Button.vue';
import Tooltip from '@/components/atoms/Tooltip.vue';
import RefreshIcon from '@/components/atoms/RefreshIcon.vue';
import Input from '@/components/atoms/Input.vue';
import Logo from '@/components/atoms/Logo.vue';
import Message from '@/components/atoms/Message.vue';
import { AlertMessage, MessageType } from '@/model/message';
import {
  refreshTokensAndRedirect,
  signOut
} from '@/service/cognitoService';
import { ContactType } from '@/service/otpService';
import {
  CRMContactType,
  confirmOtpRequest,
  getCrmContacts,
  sendOtpRequest
} from '@/service/otpService';
import { hexToRgba, maskString } from '@/utils';
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { cleanSessionStorage } from '@/plugins/session';
import { ROUTES_NAMES } from '@/constants';

export default defineComponent({
  name: 'OtpPanel',
  components: {
    Button,
    Input,
    Logo,
    Message,
    RefreshIcon,
    Tooltip
  },
  data() {
    return {
      otp: '',
      contact_value: '',
      contacts: [] as CRMContactType[],
      message: {} as AlertMessage | undefined,
      otpTypeSelected: false,
      otpCounter: 0,
      getContactValue(value: string): string {
        const contact = this.contacts.find(
          x => x.cont_value === value
        ) as CRMContactType;
        return maskString(value, contact?.cont_type);
      },
      getType(): ContactType {
        const contact = this.contacts.find(
          x => x.cont_value === this.contact_value
        ) as CRMContactType;
        return contact?.cont_type;
      }
    };
  },
  computed: {
    ...mapGetters([
      'global',
      'signInMessages',
      'signInUtils',
      'myEmail',
      'appStatus',
      'isIframe',
      'idToken',
      'accessToken',
      'refreshToken'
    ]),
    enableConfirmButton(): boolean {
      return this.otp !== '';
    },
    enableConfirmAuthType(): boolean {
      return this.contact_value !== '';
    },
    otpSelectBorderColor(): string {
      const boxColor = this.global.boxColor || '#696E79';
      return this.contact_value ? boxColor : hexToRgba(boxColor, 0.5);
    }
  },
  async mounted() {
    try {
      this.startLoading();
      this.contacts = await getCrmContacts();
      this.endLoading();
    } catch (error) {
      this.endLoading();
      this.message = {
        type: MessageType.Error,
        text: this.$t('message.get_user_contacts')
      };
    }
  },
  methods: {
    ...mapActions(['startLoading', 'endLoading']),
    async onConfirmOtp(): Promise<void> {
      try {
        this.message = undefined;
        this.startLoading();
        await confirmOtpRequest(this.otp.trim());
        this.otpCounter = 0;
        await refreshTokensAndRedirect(this.isIframe);
      } catch (error) {
        this.endLoading();
        this.message = {
          type: MessageType.Error,
          text: this.$t('message.confirm_otp_error')
        };
      }
    },
    async onSendOtp(): Promise<void> {
      try {
        this.message = undefined;
        this.startLoading();
        const contact = this.contacts.find(
          x => x.cont_value === this.contact_value
        ) as CRMContactType;
        await sendOtpRequest(contact);
        this.otpCounter++;
        this.otpTypeSelected = true;
        if(this.otpCounter > 3) {
          this.message = {
            type: MessageType.Info,
            text: this.$t('message.otp_support')
          };
        }
        this.endLoading();
      } catch (error) {
        this.endLoading();
        this.otpCounter = 0;
        this.message = {
          type: MessageType.Error,
          text: this.$t('message.send_otp_error')
        };
      }
    },
    mapOtpTypeToLabel(): string {
      const contact = this.contacts.find(
        x => x.cont_value === this.contact_value
      ) as CRMContactType;
      switch (contact?.cont_type) {
      case 'email':
        return this.$t('message.email');
      case 'phone':
        return this.$t('message.phone');
      default:
        return contact?.cont_type;
      }
    },
    async goBackToLogin(): Promise<void> {
      try {
        this.startLoading();
        await signOut();
        cleanSessionStorage();
        this.endLoading();
        this.$router.push({
          path: ROUTES_NAMES.login,
          query: {
            ...this.$route.query
          }
        });
      } catch (error) {
        this.endLoading();
      }
    }
  }
});
